import { Component, type ReactNode, type ErrorInfo } from "react";
import { flushClientErrorReports, reportClientError } from "../lib/client-error-report";

/**
 * Catches dynamic-import failures from React.lazy() and triggers a hard reload.
 *
 * Why this exists:
 * Every page route in App.tsx is `lazy(() => import("./pages/X"))`. When a new
 * Worker deploy lands, the deployed bundle has fresh chunk hashes (e.g.
 * Materials-AAAA.js → Materials-BBBB.js). Clients with the old index bundle
 * still in memory will request the old chunk on next sidebar navigation —
 * which 404s. React's Suspense catches the pending Promise but has no way to
 * handle the rejection, so the page renders nothing. User sees a blank page;
 * a hard refresh fetches the new index + the broken chunks become correct.
 *
 * This boundary detects the chunk-load error class and force-reloads. Hugo
 * reported this as "blank pages when clicking sidebar tabs, refresh fixes it"
 * 2026-05-07 10:26 AEST after yesterday's 14-PR deploy storm rotated chunk
 * hashes multiple times.
 *
 * Detection patterns cover Vite/Rollup + Webpack message shapes since
 * @cloudflare/vite-plugin can produce either depending on browser cache state.
 */

interface State {
  hasError: boolean;
  isChunkLoadError: boolean;
}

const CHUNK_ERROR_PATTERNS = [
  /Failed to fetch dynamically imported module/i,
  /Loading chunk \d+ failed/i,
  /Loading CSS chunk/i,
  /error loading dynamically imported module/i,
  /Importing a module script failed/i,
];

/** The chunk-error reload waits at least this long, and at most the max for the queued error reports to leave. */
export const CHUNK_RELOAD_MIN_MS = 800;
export const CHUNK_RELOAD_MAX_MS = 3000;

function isChunkLoadError(error: Error): boolean {
  const msg = error.message ?? "";
  return CHUNK_ERROR_PATTERNS.some((p) => p.test(msg));
}

export class ChunkLoadErrorBoundary extends Component<
  { children: ReactNode },
  State
> {
  state: State = { hasError: false, isChunkLoadError: false };

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      isChunkLoadError: isChunkLoadError(error),
    };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // #2712 CLIENTERROR: every catch reaches the server log, a chunk-load one
    // included (it says a deploy rotated chunks under an open tab). The name is
    // a literal because a production build minifies the class name. A chunk
    // catch reloads the page, so its report goes ahead of anything queued.
    const chunk = isChunkLoadError(error);
    void reportClientError("boundary", error, { boundary: "ChunkLoadErrorBoundary", urgent: chunk });
    if (chunk) {
      console.warn(
        JSON.stringify({
          event: "chunk_load_error_force_reload",
          message: error.message,
          componentStack: info.componentStack?.split("\n").slice(0, 4).join(" | "),
        })
      );
      // Reload after a short delay so the user sees the message briefly + we
      // avoid a tight reload loop if the deploy is still propagating. #2712:
      // and not before every queued report, the one above first, is on its
      // way (they wait out the reporter's send gap; keepalive carries a
      // started request through the unload), but never later than
      // CHUNK_RELOAD_MAX_MS.
      const after = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));
      void Promise.all([after(CHUNK_RELOAD_MIN_MS), Promise.race([flushClientErrorReports(), after(CHUNK_RELOAD_MAX_MS)])]).then(() =>
        window.location.reload(),
      );
    } else {
      // Non-chunk error — log it but let the user retry manually
      console.error("Uncaught error:", error, info);
    }
  }

  render(): ReactNode {
    if (this.state.hasError && this.state.isChunkLoadError) {
      return (
        <div className="flex h-full min-h-[200px] items-center justify-center p-6">
          <div className="rounded-lg border border-border bg-card px-6 py-4 text-sm text-muted-foreground">
            <p className="font-medium text-foreground">Updating to the latest version…</p>
            <p className="mt-1 text-xs">
              A new app version is available. Reloading now.
            </p>
          </div>
        </div>
      );
    }

    if (this.state.hasError) {
      // Generic fallback — something else broke
      return (
        <div className="flex h-full min-h-[200px] items-center justify-center p-6">
          <div className="rounded-lg border border-destructive/30 bg-destructive/5 px-6 py-4 text-sm">
            <p className="font-medium text-destructive">Something went wrong</p>
            <p className="mt-1 text-xs text-muted-foreground">
              Try refreshing the page. If it persists, contact an administrator.
            </p>
            <button
              onClick={() => this.setState({ hasError: false, isChunkLoadError: false })}
              className="mt-3 rounded-md border border-border px-3 py-1 text-xs hover:bg-muted"
            >
              Try again
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
